import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocale } from "../../hooks/useLocale";
import { useTheme } from "../../hooks/useTheme";

interface ApplicantsPaginationProps {
  page: number;
  totalPages: number;
  onChange: (nextPage: number) => void;
}

const SIBLINGS = 1;
const BOUNDARY = 1;

function buildPageItems(page: number, totalPages: number): (number | "…")[] {
  if (totalPages <= 1) return [];

  const totalItems = SIBLINGS * 2 + BOUNDARY * 2 + 3;
  if (totalPages <= totalItems) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(page - SIBLINGS, BOUNDARY + 2);
  const rightSibling = Math.min(page + SIBLINGS, totalPages - BOUNDARY - 1);
  const showLeftDots = leftSibling > BOUNDARY + 2;
  const showRightDots = rightSibling < totalPages - BOUNDARY - 1;

  const items: (number | "…")[] = [];
  for (let i = 1; i <= BOUNDARY; i += 1) items.push(i);

  if (showLeftDots) items.push("…");
  else for (let i = BOUNDARY + 1; i < leftSibling; i += 1) items.push(i);

  for (let i = leftSibling; i <= rightSibling; i += 1) items.push(i);

  if (showRightDots) items.push("…");
  else
    for (let i = rightSibling + 1; i <= totalPages - BOUNDARY; i += 1)
      items.push(i);

  for (let i = totalPages - BOUNDARY + 1; i <= totalPages; i += 1) items.push(i);

  return items;
}

export function ApplicantsPagination({
  page,
  totalPages,
  onChange,
}: ApplicantsPaginationProps) {
  const { t } = useTranslation("common");
  const { theme } = useTheme();
  const { isRTL } = useLocale();

  const items = useMemo(
    () => buildPageItems(page, totalPages),
    [page, totalPages],
  );

  if (totalPages <= 1) return null;

  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  const baseButton = {
    minWidth: 36,
    height: 36,
    paddingInline: 10,
    borderRadius: 999,
    border: `1px solid ${theme.borderSubtle}`,
    backgroundColor: theme.surface,
    color: theme.text,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  } as const;

  const disabledStyle = { opacity: 0.4, cursor: "default" as const };

  const goPrev = () => {
    if (page > 1) onChange(page - 1);
  };
  const goNext = () => {
    if (page < totalPages) onChange(page + 1);
  };

  const prevDisabled = page <= 1;
  const nextDisabled = page >= totalPages;

  return (
    <nav
      aria-label={t("jobApplicants.pagination.pageOf", {
        page,
        total: totalPages,
      })}
      style={{
        display: "flex",
        flexDirection: isRTL ? "row-reverse" : "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        paddingBlock: 16,
      }}
    >
      <button
        type="button"
        onClick={goPrev}
        disabled={prevDisabled}
        aria-label={t("jobApplicants.pagination.previous")}
        style={{ ...baseButton, ...(prevDisabled ? disabledStyle : null) }}
      >
        <PrevIcon size={16} strokeWidth={2.1} />
      </button>

      {items.map((item, idx) =>
        item === "…" ? (
          <span
            key={`gap-${idx}`}
            style={{
              minWidth: 28,
              textAlign: "center",
              color: theme.textTertiary,
              fontSize: 13,
            }}
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            aria-current={item === page ? "page" : undefined}
            style={{
              ...baseButton,
              ...(item === page
                ? {
                    backgroundColor: theme.text,
                    color: theme.background,
                    borderColor: theme.text,
                  }
                : null),
            }}
          >
            {item}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={goNext}
        disabled={nextDisabled}
        aria-label={t("jobApplicants.pagination.next")}
        style={{ ...baseButton, ...(nextDisabled ? disabledStyle : null) }}
      >
        <NextIcon size={16} strokeWidth={2.1} />
      </button>
    </nav>
  );
}
